import { useDispatch, useSelector } from 'react-redux'
import { useMemo } from 'react'
import {
  EndpointArgs,
  InferQueryDefinition,
  QueryDefinition,
  UseMutationResult
} from '../../../store/serverApi/types'

import {
  QueryActionCreatorResult,
  QueryResultSelectorResult,
  SubscriptionOptions,
  UseLazyQuery,
  UseQuery,
  UseQueryStateOptions
} from '../../../types/rtkInternals'

import { RootState } from '../../../reducers'
import type { AnyAction, ThunkAction } from '@reduxjs/toolkit'
import { FeedbotDispatch } from '../../../store/types'
import { createMemoizedTransformer } from './utils'

type ConstructorParameters<Endpoint extends QueryDefinition> = {
  endpoint: QueryDefinition
  argsTransformer?: (state: RootState, newParams: any) => EndpointArgs<Endpoint>
  skipCriteria?: (endpointArgs: EndpointArgs<Endpoint>) => boolean
}

type Types<Endpoint extends QueryDefinition> = {
  endpoint: Endpoint
  endpointDefinition: InferQueryDefinition<Endpoint>
  nativeArgs: EndpointArgs<Endpoint>
  outerArgs: any
}

type GetTypesFromConstructorParams<CP extends ConstructorParameters<QueryDefinition>> = {
  endpoint: CP['endpoint']
  endpointDefinition: InferQueryDefinition<CP['endpoint']>
  nativeArgs: EndpointArgs<CP['endpoint']>
  outerArgs: CP['argsTransformer'] extends Function
    ? Parameters<CP['argsTransformer']>[1]
    : EndpointArgs<CP['endpoint']>
}

class QueryEndpointHelper<
  T extends Types<any>,
  ConstructorParams extends ConstructorParameters<T['endpoint']>
> {
  private endpoint: T['endpoint']
  private readonly params: ConstructorParams
  private readonly transformArgs: (state: RootState, newParams: T['outerArgs']) => T['nativeArgs']
  private readonly shouldSkip: (endpointArgs: EndpointArgs<T['endpoint']>) => boolean

  constructor(params: ConstructorParams) {
    this.params = params
    this.endpoint = params.endpoint
    this.transformArgs = params.argsTransformer ?? ((state, params) => params)
    this.shouldSkip = params?.skipCriteria ?? (() => false)
  }

  public static new = <CP extends ConstructorParameters<any>>(params: CP) => {
    // This is a TypeScript workaround - class generic types cannot be inferred from constructor parameter.
    // Using this method instead of `new QueryEndpointHelper()` makes it possible to infer.
    return new QueryEndpointHelper<GetTypesFromConstructorParams<CP>, CP>(params)
  }

  // --- Class management methods ---

  public addArgsTransformer = <OuterParams>(
    argsTransformer: (state: RootState, params: OuterParams) => T['nativeArgs']
  ) => {
    return QueryEndpointHelper.new({ ...this.params, argsTransformer })
  }

  public addSkipCondition = (skipCriteria: typeof this.shouldSkip) => {
    return QueryEndpointHelper.new({ ...this.params, skipCriteria })
  }

  // --- Api control methods ---

  public select = (outerArgs: T['outerArgs']) => {
    return (state: RootState) => {
      const innerArgs = this.transformArgs(state, outerArgs)
      return this.endpoint.select(innerArgs)(state as any) as QueryResultSelectorResult<
        T['endpointDefinition']
      >
    }
  }

  public selectData = (outerArgs: T['outerArgs']) => {
    return (state: RootState) => this.select(outerArgs)(state)?.data
  }

  public initiate = (
    outerArgs: T['outerArgs']
  ): ThunkAction<QueryActionCreatorResult<T['endpointDefinition']>, any, void, AnyAction> => {
    // CAREFUL: DO NOT USE ASYNC FUNCTION HERE! Async function returns its own promise.
    // We need to return the native rtkq promise to have access to the `.unwrap()` method
    // @ts-ignore
    return (dispatch: FeedbotDispatch, getState: () => RootState) => {
      const innerArgs = this.transformArgs(getState(), outerArgs)

      if (this.shouldSkip(innerArgs)) {
        return
      }

      return dispatch(this.endpoint.initiate(innerArgs))
    }
  }

  public useQuery = (outerArgs: T['outerArgs'], options?: UseQueryStateOptions<any, any>) => {
    const transformArgs = this.transformArgs
    const memoizedTransformer = useMemo(
      () => createMemoizedTransformer(transformArgs),
      [transformArgs]
    )

    const args = useSelector(state => memoizedTransformer(state as any, outerArgs))
    const useQuery = this.endpoint.useQuery as UseQuery<T['endpointDefinition']>
    // @ts-ignore as void is required, otherwise return type inference breaks :///
    return useQuery(args as any, { skip: this.shouldSkip(args), ...options } as void)
  }

  public useQueryData(outerArgs: T['outerArgs'], options?: UseQueryStateOptions<any, any>) {
    return this.useQuery(outerArgs, options)?.data
  }

  public useLazyQuery = (
    options?: SubscriptionOptions & Omit<UseQueryStateOptions<T['endpointDefinition'], any>, 'skip'>
  ) => {
    const dispatch = useDispatch()
    const useLazyQuery = this.endpoint.useLazyQuery as UseLazyQuery<T['endpointDefinition']>

    const [nativeTrigger, nativeResult, lastPromise] = useLazyQuery(options)

    return [
      (outerArgs: T['outerArgs']) => {
        // @ts-ignore
        const getState: () => RootState = dispatch((dispatch, getState) => getState)
        const args = this.transformArgs(getState(), outerArgs)
        return nativeTrigger(args as any)
      },
      nativeResult as UseMutationResult<T['endpoint']>,
      lastPromise
    ] as const
  }
}

export function createQueryHelper<Endpoint extends QueryDefinition>(endpoint: Endpoint) {
  return QueryEndpointHelper.new({ endpoint })
}
